import type { NormalizedSupplierResult, SupplierSourceKey } from "@/lib/normalizeSupplierResult";
import {
  classifyOfferIdentity,
  normalizeMpn,
  type OfferMatchType,
} from "./part-identity.js";
import { scoreDiscoveryResult, type SearchIntent } from "./search-intent.js";
import { matchesAcceptedOfferConditions, normalizedOfferCondition } from "./offer-condition.js";
import type { MarketScanEvidence, MarketScanIdentityConfidence } from "@/lib/market-scan";

export type OfferSourceType = "marketplace" | "supplier_api" | "supplier_aggregator" | "supplier_feed" | "external_listing";
export type OfferCheckoutMode = "control_exchange" | "supplier_redirect" | "request_confirmation";
export type AvailabilityConfidence = "confirmed" | "live" | "feed" | "unconfirmed";
export type OfferIdentityConfidence = "verified" | "mpn_only" | "possible";
export type ScanConfidence = "high" | "medium" | "review";
export type RankingMode = "best" | "delivered" | "advertised" | "cheapest";

export interface GbpExchangeRates {
  base: "GBP";
  asOf: string;
  fetchedAt: string;
  rates: Record<string, number>;
}

export interface DeliveredCostCompleteness {
  shipping: boolean;
  vat: boolean;
  importCharges: boolean;
}

export interface SourcingOffer {
  id: string;
  source: string;
  /** Raw supplier identity retained for outbound click and conversion attribution. */
  sourceId: string;
  sourceKey: SupplierSourceKey;
  sourceType: OfferSourceType;
  checkoutMode: OfferCheckoutMode;
  matchType: OfferMatchType;
  identityConfidence: OfferIdentityConfidence;
  manufacturerMatch: boolean | null;
  relevanceScore: number | null;
  matchExplanation: string | null;
  manufacturer: string | null;
  mpn: string;
  revision: string | null;
  description: string | null;
  condition: string;
  availableQuantity: number | null;
  requestedQuantity: number;
  minimumOrderQuantity: number | null;
  orderIncrement: number | null;
  orderQuantity: number;
  packaging: string | null;
  packageQuantity: number | null;
  unitPrice: number | null;
  quantityAdjustedUnitPrice: number | null;
  currency: string;
  originalSubtotal: number | null;
  /** Item subtotal plus every fee the supplier reported, before FX conversion. */
  originalKnownTotal: number | null;
  estimatedGbpSubtotal: number | null;
  shippingCost: number | null;
  vatCost: number | null;
  importCharges: number | null;
  /** Sum of every cost component the supplier actually reported. */
  estimatedGbpKnownTotal: number | null;
  /** Delivered total; null until shipping, VAT, and import costs are all known. */
  estimatedGbpTotal: number | null;
  costCompleteness: DeliveredCostCompleteness;
  costIsComplete: boolean;
  fxAsOf: string | null;
  dispatchAt: string | null;
  deliveryAt: string | null;
  warrantyMonths: number | null;
  returnsAccepted: boolean | null;
  availabilityConfidence: AvailabilityConfidence;
  availabilityAt: string | null;
  observedAt: string;
  availabilityText: string | null;
  sellerVerified: boolean;
  location: string | null;
  productUrl: string;
  imageUrl: string | null;
  datasheetUrl: string | null;
  listingId: string | null;
  partSlug: string | null;
  aggregatedBy: string | null;
  sourceEvidence: MarketScanEvidence | null;
  sourceIdentityConfidence: MarketScanIdentityConfidence | null;
  outboundOfferReference: string | null;
  outboundCapability: string | null;
  outboundCapabilityExpiresAt: string | null;
  scanConfidence: ScanConfidence;
  scanConfidenceScore: number;
  scanWarnings: string[];
  dataCompletenessScore: number;
}

export interface OfferFilters {
  conditions?: string[];
  confirmedOnly?: boolean;
  deliveryBy?: string;
  suppliers?: string[];
  location?: string;
  warrantyRequired?: boolean;
  /** Parts subtotal for the payable quantity, excluding additional costs. */
  maxGbpSubtotal?: number | null;
  /** Delivered total; requires all cost components to be known. */
  maxGbpTotal?: number | null;
  controlExchangeCheckoutOnly?: boolean;
}

const CONFIDENCE_WEIGHT: Record<AvailabilityConfidence, number> = {
  confirmed: 4,
  live: 3,
  feed: 2,
  unconfirmed: 1,
};

const MATCH_WEIGHT: Record<OfferMatchType, number> = {
  exact: 3,
  variant: 2,
  fuzzy: 1,
};

function selectQuantityPrice(result: NormalizedSupplierResult, quantity: number): number | null {
  const breaks = [...result.priceBreaks]
    .filter((priceBreak) => priceBreak.quantity <= quantity && Number.isFinite(priceBreak.unitPrice))
    .sort((a, b) => b.quantity - a.quantity);
  return breaks[0]?.unitPrice ?? result.unitPrice;
}

function positiveInteger(value: number | null | undefined): number | null {
  return value != null && Number.isFinite(value) && value > 0
    ? Math.max(1, Math.floor(value))
    : null;
}

/**
 * Returns the smallest order that satisfies both MOQ and supplier order-multiple
 * constraints. An order increment is treated as a strict quantity multiple.
 */
export function calculateOrderQuantity(
  requestedQuantity: number,
  minimumOrderQuantity?: number | null,
  orderIncrement?: number | null,
): number {
  const requested = positiveInteger(requestedQuantity) ?? 1;
  const minimum = positiveInteger(minimumOrderQuantity) ?? 1;
  const increment = positiveInteger(orderIncrement) ?? 1;
  const minimumSatisfied = Math.max(requested, minimum);
  return increment > 1
    ? Math.ceil(minimumSatisfied / increment) * increment
    : minimumSatisfied;
}

function addDays(iso: string, days: number | null | undefined): string | null {
  if (days == null || !Number.isFinite(days)) return null;
  const date = new Date(iso);
  date.setUTCDate(date.getUTCDate() + Math.max(0, Math.floor(days)));
  return date.toISOString();
}

function toGbp(amount: number | null, currency: string, fx: GbpExchangeRates | null): number | null {
  if (amount == null) return null;
  if (currency === "GBP") return amount;
  const rate = fx?.rates[currency];
  return rate && rate > 0 ? amount * rate : null;
}

function sourceDefaults(result: NormalizedSupplierResult): {
  sourceType: OfferSourceType;
  checkoutMode: OfferCheckoutMode;
  confidence: AvailabilityConfidence;
} {
  if (result.sourceKey === "control_exchange") {
    return {
      sourceType: "marketplace",
      checkoutMode: "control_exchange",
      confidence: result.availabilityConfidence ?? "unconfirmed",
    };
  }
  if (result.sourceKey === "ebay" || result.sourceKey === "web") {
    return {
      sourceType: "external_listing",
      checkoutMode: result.unitPrice == null ? "request_confirmation" : "supplier_redirect",
      confidence: "unconfirmed",
    };
  }
  if (result.sourceKey === "oemsecrets" || result.sourceKey === "nexar") {
    return {
      sourceType: "supplier_aggregator",
      checkoutMode: result.unitPrice == null ? "request_confirmation" : "supplier_redirect",
      confidence: "live",
    };
  }
  return {
    sourceType: "supplier_api",
    checkoutMode: result.unitPrice == null ? "request_confirmation" : "supplier_redirect",
    confidence: "live",
  };
}

function boundedScore(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function ageHours(value: string | null | undefined, now: string): number | null {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  const reference = new Date(now).getTime();
  return Number.isFinite(timestamp) && Number.isFinite(reference)
    ? Math.max(0, reference - timestamp) / 3_600_000
    : null;
}

export function scanEvidenceLabel(evidence: MarketScanEvidence | null): string {
  if (evidence === "structured_data") return "Structured supplier product data";
  if (evidence === "labelled_product_page") return "Labelled supplier product page";
  if (evidence === "supplier_feed") return "Supplier stock feed";
  return "Source evidence not classified";
}

export function assessOfferScanQuality(input: {
  matchType: OfferMatchType;
  manufacturerMatch: boolean | null;
  sourceEvidence: MarketScanEvidence | null;
  sourceIdentityConfidence: MarketScanIdentityConfidence | null;
  result: NormalizedSupplierResult;
  observedAt: string;
  searchedAt: string;
}): {
  confidence: ScanConfidence;
  score: number;
  warnings: string[];
  completeness: number;
} {
  const { result } = input;
  const warnings: string[] = [];
  let score = input.matchType === "exact" ? 50 : input.matchType === "variant" ? 24 : 5;
  if (input.sourceIdentityConfidence === "explicit") score += 16;
  else if (input.sourceIdentityConfidence === "structured") score += 13;
  else if (input.sourceIdentityConfidence === "inferred") {
    score -= 20;
    warnings.push("Supplier product identity was inferred");
  }
  if (input.manufacturerMatch === true) score += 10;
  else if (input.manufacturerMatch === false) {
    score -= 35;
    warnings.push("Manufacturer does not agree with the request");
  } else if (input.matchType === "exact") {
    warnings.push("Manufacturer not independently confirmed");
  }
  if (input.sourceEvidence) score += 5;
  if (result.productUrl) score += 4;
  if (result.description) score += 3;
  if (result.unitPrice != null) score += 4;
  else warnings.push("Supplier price not provided");
  if (result.availability != null) score += 4;
  else warnings.push("Stock quantity not provided");
  const observationAge = ageHours(input.observedAt, input.searchedAt);
  if (observationAge != null && observationAge <= 24) score += 4;
  else if (observationAge != null) warnings.push("Supplier observation is older than 24 hours");
  if (input.matchType !== "exact") warnings.push(
    input.matchType === "variant" ? "Possible variant—not confirmed compatible" : "Part identity requires review",
  );

  const procurementFields = [
    result.unitPrice,
    result.availability,
    result.minimumOrderQuantity,
    result.orderIncrement,
    result.packaging,
    result.description,
    result.imageUrl,
    result.datasheetUrl,
    result.shippingCost,
    result.vatIncluded,
    result.leadTimeDays,
    result.warrantyMonths,
    result.returnsAllowed,
    result.location,
  ];
  const completeness = boundedScore((procurementFields.filter((value) => value !== null && value !== undefined && value !== "").length / procurementFields.length) * 100);
  const bounded = boundedScore(score);
  return {
    confidence: bounded >= 85 ? "high" : bounded >= 65 ? "medium" : "review",
    score: bounded,
    warnings,
    completeness,
  };
}

export function toSourcingOffer(
  result: NormalizedSupplierResult,
  options: {
    query: string;
    quantity: number;
    fx: GbpExchangeRates | null;
    searchedAt?: string;
    searchIntent?: SearchIntent | null;
  },
): SourcingOffer {
  const quantity = positiveInteger(options.quantity) ?? 1;
  const searchedAt = options.searchedAt ?? new Date().toISOString();
  const defaults = sourceDefaults(result);
  const minimumOrderQuantity = positiveInteger(result.minimumOrderQuantity);
  const orderIncrement = positiveInteger(result.orderIncrement);
  const packageQuantity = positiveInteger(result.packageQuantity);
  const orderQuantity = calculateOrderQuantity(quantity, minimumOrderQuantity, orderIncrement);
  const adjustedUnitPrice = selectQuantityPrice(result, orderQuantity);
  const subtotal = adjustedUnitPrice == null ? null : adjustedUnitPrice * orderQuantity;
  const shippingCost = result.shippingCost ?? null;
  const vatCost = result.vatCost ?? null;
  const importCharges = result.importCharges ?? null;
  const completeness = {
    shipping: result.shippingCost != null,
    vat: result.vatIncluded === true || result.vatCost != null,
    importCharges: result.importCharges != null,
  };
  // Fee coverage alone is not a delivered price. A supplier that reports all
  // fee flags but omits the item price must not receive the completeness bonus
  // or sort ahead of a genuinely comparable offer.
  const feesAreComplete = subtotal != null && Object.values(completeness).every(Boolean);
  const knownTotal = subtotal == null
    ? null
    : subtotal + (shippingCost ?? 0) + (vatCost ?? 0) + (importCharges ?? 0);
  const estimatedGbpKnownTotal = toGbp(knownTotal, result.currency, options.fx);
  // A delivered total is only comparable in this GBP-ranked market when its
  // currency can actually be converted. Fee completeness alone is not enough.
  const costIsComplete = feesAreComplete && estimatedGbpKnownTotal != null;
  const deliveryAt = result.deliveryAt ?? addDays(searchedAt, result.leadTimeDays);
  const identity = classifyOfferIdentity({
    requestedMpn: options.searchIntent?.partNumber ?? options.query,
    requestedManufacturer: options.searchIntent?.manufacturer,
    candidateMpn: result.partNumber,
    candidateManufacturer: result.manufacturer,
    candidateTitle: result.partNumber,
  });
  const discoveryMatch = options.searchIntent?.mode === "product_discovery"
    ? scoreDiscoveryResult(options.searchIntent, {
      partNumber: result.partNumber,
      manufacturer: result.manufacturer,
      description: result.description,
    })
    : null;
  const observedAt = result.availabilityAt ?? searchedAt;
  const scanQuality = assessOfferScanQuality({
    matchType: identity.matchType,
    manufacturerMatch: identity.manufacturerMatch,
    sourceEvidence: result.sourceEvidence ?? null,
    sourceIdentityConfidence: result.sourceIdentityConfidence ?? null,
    result,
    observedAt,
    searchedAt,
  });

  return {
    id: result.listingId ?? `${result.sourceKey}:${result.sourceLabel}:${result.partNumber}:${result.productUrl}`,
    source: result.sourceLabel,
    sourceId: result.sourceId ?? result.sourceKey,
    sourceKey: result.sourceKey,
    sourceType: defaults.sourceType,
    checkoutMode: defaults.checkoutMode,
    matchType: identity.matchType,
    identityConfidence: identity.matchType !== "exact" || result.sourceIdentityConfidence === "inferred"
      ? "possible"
      : identity.manufacturerMatch === true
        ? "verified"
        : "mpn_only",
    manufacturerMatch: identity.manufacturerMatch,
    relevanceScore: discoveryMatch?.score ?? null,
    matchExplanation: discoveryMatch?.reasons.join(" • ") || identity.explanation,
    manufacturer: result.manufacturer ?? null,
    mpn: identity.candidateMpn || result.partNumber,
    revision: result.revision ?? null,
    description: result.description ?? null,
    condition: result.condition ?? "Unknown",
    availableQuantity: result.availability,
    requestedQuantity: quantity,
    minimumOrderQuantity,
    orderIncrement,
    orderQuantity,
    packaging: result.packaging?.trim() || null,
    packageQuantity,
    unitPrice: result.unitPrice,
    quantityAdjustedUnitPrice: adjustedUnitPrice,
    currency: result.currency,
    originalSubtotal: subtotal,
    originalKnownTotal: knownTotal,
    estimatedGbpSubtotal: toGbp(subtotal, result.currency, options.fx),
    shippingCost,
    vatCost,
    importCharges,
    estimatedGbpKnownTotal,
    estimatedGbpTotal: costIsComplete ? estimatedGbpKnownTotal : null,
    costCompleteness: completeness,
    costIsComplete,
    fxAsOf: options.fx?.asOf ?? null,
    dispatchAt: result.dispatchAt ?? null,
    deliveryAt,
    warrantyMonths: result.warrantyMonths ?? null,
    returnsAccepted: result.returnsAllowed ?? null,
    availabilityConfidence: result.availabilityConfidence ?? defaults.confidence,
    availabilityAt: result.availabilityAt ?? (defaults.confidence === "live" ? searchedAt : null),
    observedAt,
    availabilityText: result.availabilityText ?? null,
    sellerVerified: result.sellerVerified ?? false,
    location: result.location ?? null,
    productUrl: result.productUrl,
    imageUrl: result.imageUrl ?? null,
    datasheetUrl: result.datasheetUrl ?? null,
    listingId: result.listingId ?? null,
    partSlug: result.partSlug ?? null,
    aggregatedBy: result.sourceKey === "oemsecrets"
      ? "OEMsecrets"
      : result.sourceKey === "nexar"
        ? "Nexar"
        : null,
    sourceEvidence: result.sourceEvidence ?? null,
    sourceIdentityConfidence: result.sourceIdentityConfidence ?? null,
    outboundOfferReference: result.outboundOfferReference ?? null,
    outboundCapability: result.outboundCapability ?? null,
    outboundCapabilityExpiresAt: result.outboundCapabilityExpiresAt ?? null,
    scanConfidence: scanQuality.confidence,
    scanConfidenceScore: scanQuality.score,
    scanWarnings: scanQuality.warnings,
    dataCompletenessScore: scanQuality.completeness,
  };
}

function dateValue(value: string | null): number {
  if (!value) return Number.POSITIVE_INFINITY;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY;
}

export function normalizeOfferCondition(value: string): string {
  return normalizedOfferCondition(value);
}

export function offerConditionLabel(value: string): string {
  const normalized = normalizeOfferCondition(value);
  const compact = value.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  if (normalized === "new" && ["newopenbox", "newother", "newotherseedetails"].includes(compact)) {
    return "New, open box";
  }
  const labels: Record<string, string> = {
    new: "New",
    factory_sealed: "New, sealed",
    used: "Used",
    refurbished: "Refurbished",
    for_parts: "For parts or repair",
    unknown: "Not stated",
    conditionnotstated: "Not stated",
    condition_not_stated: "Not stated",
    not_stated: "Not stated",
    not_specified: "Not stated",
    unspecified: "Not stated",
    "": "Not stated",
  };
  return labels[normalized] ?? normalized.replace(/_/g, " ").replace(/^./, (first) => first.toUpperCase());
}

function compareIdentity(a: SourcingOffer, b: SourcingOffer): number {
  if (a.relevanceScore != null || b.relevanceScore != null) return 0;
  const matchOrder = MATCH_WEIGHT[b.matchType] - MATCH_WEIGHT[a.matchType];
  if (matchOrder !== 0) return matchOrder;
  const confidenceWeight: Record<OfferIdentityConfidence, number> = {
    verified: 3,
    mpn_only: 2,
    possible: 1,
  };
  return confidenceWeight[b.identityConfidence] - confidenceWeight[a.identityConfidence];
}

function observationFreshnessScore(observedAt: string | null): number {
  if (!observedAt) return 0;
  const timestamp = new Date(observedAt).getTime();
  if (!Number.isFinite(timestamp)) return 0;
  const ageHours = Math.max(0, Date.now() - timestamp) / 3_600_000;
  if (ageHours <= 0.25) return 60;
  if (ageHours <= 1) return 50;
  if (ageHours <= 6) return 35;
  if (ageHours <= 24) return 15;
  if (ageHours <= 72) return -40;
  return -250;
}

function procurementAvailabilityRank(offer: Pick<SourcingOffer, "availableQuantity" | "orderQuantity">): number {
  if (offer.availableQuantity == null) return 1;
  return offer.availableQuantity >= offer.orderQuantity ? 0 : 2;
}

function isDistributorOffer(offer: Pick<SourcingOffer, "source" | "sourceKey">): boolean {
  if (offer.sourceKey === "control_exchange" || offer.sourceKey === "ebay") return false;
  if (["siemens", "schneider", "netilion"].includes(offer.sourceKey)) return false;
  return true;
}

function compareBestRetailerPriority(a: SourcingOffer, b: SourcingOffer): number {
  const aViableDistributor = isDistributorOffer(a) && procurementAvailabilityRank(a) < 2;
  const bViableDistributor = isDistributorOffer(b) && procurementAvailabilityRank(b) < 2;
  if (a.sourceKey === "ebay" && bViableDistributor) return 1;
  if (b.sourceKey === "ebay" && aViableDistributor) return -1;
  return 0;
}

function bestScore(
  offer: SourcingOffer,
  lowestCompletePrice: number | null,
  lowestIncompleteKnownPrice: number | null,
): number {
  const stockScore = offer.availableQuantity == null
    ? 25
    : offer.availableQuantity >= offer.orderQuantity
      ? 40
      : -2_000;
  const priced = offer.estimatedGbpKnownTotal != null;
  const price = offer.costIsComplete ? offer.estimatedGbpTotal : offer.estimatedGbpKnownTotal;
  const lowestComparablePrice = offer.costIsComplete ? lowestCompletePrice : lowestIncompleteKnownPrice;
  // An incomplete sticker price is useful evidence, but it must not overwhelm
  // stock, freshness, scan quality, or an actually comparable delivered price.
  const priceWeight = offer.relevanceScore == null
    ? offer.costIsComplete ? 360 : 60
    : offer.costIsComplete ? 180 : 30;
  const priceScore = price == null || lowestComparablePrice == null
    ? 0
    : priceWeight * Math.min(1, lowestComparablePrice / price);
  const missingCostComponents = Object.values(offer.costCompleteness).filter((known) => !known).length;
  const costConfidenceScore = offer.costIsComplete ? 260 : -40 * missingCostComponents;
  const deliveryDays = offer.deliveryAt == null
    ? null
    : Math.ceil((dateValue(offer.deliveryAt) - Date.now()) / 86_400_000);
  const deliveryScore = deliveryDays == null || deliveryDays < 0 ? 0 : Math.max(0, 30 - deliveryDays);

  return (
    (offer.relevanceScore == null ? MATCH_WEIGHT[offer.matchType] * 1_000 : offer.relevanceScore * 12) +
    (offer.identityConfidence === "verified" ? 20 : offer.identityConfidence === "mpn_only" ? 10 : 0) +
    CONFIDENCE_WEIGHT[offer.availabilityConfidence] * 15 +
    stockScore +
    observationFreshnessScore(offer.availabilityAt) +
    (offer.sellerVerified ? 20 : 0) +
    costConfidenceScore +
    offer.scanConfidenceScore * 1.5 +
    (priced ? 10 : 0) +
    priceScore +
    deliveryScore
  );
}

export function isRelevantSearchOffer(offer: SourcingOffer): boolean {
  return offer.relevanceScore == null || offer.relevanceScore >= 50;
}

export function offerPriceLabel(offer: Pick<SourcingOffer, "costIsComplete" | "currency" | "originalSubtotal" | "originalKnownTotal" | "estimatedGbpKnownTotal" | "estimatedGbpTotal">): string {
  if (offer.costIsComplete && offer.estimatedGbpTotal != null) return "Estimated delivered total";
  if (offer.estimatedGbpKnownTotal != null) return "Known cost—fees may apply";
  if (offer.originalKnownTotal != null) return `${offer.currency} known cost—GBP conversion unavailable`;
  if (offer.originalSubtotal != null) return `${offer.currency} item subtotal—GBP conversion unavailable`;
  return "Supplier price not listed";
}

/** Safe card/display amount: complete delivered cost when known, otherwise only reported costs. */
export function offerDisplayGbpAmount(
  offer: Pick<SourcingOffer, "estimatedGbpKnownTotal" | "estimatedGbpTotal">,
): number | null {
  return offer.estimatedGbpTotal ?? offer.estimatedGbpKnownTotal;
}

export function rankOffers(offers: SourcingOffer[], mode: RankingMode): SourcingOffer[] {
  const lowestCompletePriceByMatchType = new Map<OfferMatchType, number>();
  const lowestIncompleteKnownPriceByMatchType = new Map<OfferMatchType, number>();
  for (const offer of offers) {
    if (offer.costIsComplete && offer.estimatedGbpTotal != null) {
      const current = lowestCompletePriceByMatchType.get(offer.matchType);
      if (current == null || offer.estimatedGbpTotal < current) {
        lowestCompletePriceByMatchType.set(offer.matchType, offer.estimatedGbpTotal);
      }
    }
    if (!offer.costIsComplete && offer.estimatedGbpKnownTotal != null) {
      const current = lowestIncompleteKnownPriceByMatchType.get(offer.matchType);
      if (current == null || offer.estimatedGbpKnownTotal < current) {
        lowestIncompleteKnownPriceByMatchType.set(offer.matchType, offer.estimatedGbpKnownTotal);
      }
    }
  }

  return [...offers].sort((a, b) => {
    // Lowest price follows the amount shown to the buyer. Identity, stock and
    // complete-cost evidence remain visible and break ties, but cannot place a
    // larger displayed amount ahead of a smaller one in this explicit mode.
    if (mode === "cheapest") {
      const aCost = offerDisplayGbpAmount(a);
      const bCost = offerDisplayGbpAmount(b);
      if (aCost != null || bCost != null) {
        const costOrder = (aCost ?? Number.POSITIVE_INFINITY) - (bCost ?? Number.POSITIVE_INFINITY);
        if (costOrder !== 0) return costOrder;
      }
    }

    const identityOrder = compareIdentity(a, b);
    if (identityOrder !== 0) return identityOrder;

    // In the default comparison, exact offers from viable distributors are
    // more procurement-reliable than marketplace listings from eBay. Identity
    // remains the first gate, and price-led modes retain their own ordering.
    if (mode === "best") {
      const retailerOrder = compareBestRetailerPriority(a, b);
      if (retailerOrder !== 0) return retailerOrder;
    }

    if (mode === "delivered") {
      const availabilityOrder = procurementAvailabilityRank(a) - procurementAvailabilityRank(b);
      if (availabilityOrder !== 0) return availabilityOrder;
      if (a.costIsComplete !== b.costIsComplete) return a.costIsComplete ? -1 : 1;
      const aComparableCost = a.costIsComplete ? a.estimatedGbpTotal : a.estimatedGbpKnownTotal;
      const bComparableCost = b.costIsComplete ? b.estimatedGbpTotal : b.estimatedGbpKnownTotal;
      if (aComparableCost != null || bComparableCost != null) {
        const costOrder = (aComparableCost ?? Number.POSITIVE_INFINITY) -
          (bComparableCost ?? Number.POSITIVE_INFINITY);
        if (costOrder !== 0) return costOrder;
      }
    }

    if (mode === "advertised") {
      if (a.estimatedGbpSubtotal != null || b.estimatedGbpSubtotal != null) {
        const advertisedOrder = (a.estimatedGbpSubtotal ?? Number.POSITIVE_INFINITY) -
          (b.estimatedGbpSubtotal ?? Number.POSITIVE_INFINITY);
        if (advertisedOrder !== 0) return advertisedOrder;
      }
    }

    const scoreOrder = bestScore(
      b,
      lowestCompletePriceByMatchType.get(b.matchType) ?? null,
      lowestIncompleteKnownPriceByMatchType.get(b.matchType) ?? null,
    ) - bestScore(
      a,
      lowestCompletePriceByMatchType.get(a.matchType) ?? null,
      lowestIncompleteKnownPriceByMatchType.get(a.matchType) ?? null,
    );
    if (scoreOrder !== 0) return scoreOrder;
    return a.source.localeCompare(b.source) || a.id.localeCompare(b.id);
  });
}

function normalizedRetailerKey(offer: Pick<SourcingOffer, "source" | "sourceKey">): string {
  const value = offer.source.toLowerCase().replace(/[^a-z0-9]/g, "")
    .replace(/(?:limited|ltd|inc|llc|plc|corporation|corp)$/g, "");
  const knownRetailers: Array<[RegExp, string]> = [
    [/^digikey(?:electronics)?$/, "digikey"],
    [/^mouser(?:electronics)?$/, "mouser"],
    [/^rs(?:components|online)?$/, "rs"],
    [/^radwell(?:international)?$/, "radwell"],
    [/^newark$/, "newark"],
    [/^farnell(?:element14)?$/, "farnell"],
    [/^tme(?:electroniccomponents)?$/, "tme"],
    [/^arrow(?:electronics)?$/, "arrow"],
  ];
  return knownRetailers.find(([pattern]) => pattern.test(value))?.[1]
    ?? (offer.sourceKey === "oemsecrets" ? value : value || offer.sourceKey);
}

function isAggregatorOffer(offer: Pick<SourcingOffer, "sourceKey">): boolean {
  return offer.sourceKey === "oemsecrets" || offer.sourceKey === "nexar";
}

function normalizedPackaging(value: string | null): string | null {
  const normalized = value?.trim().toLowerCase().replace(/[^a-z0-9]+/g, "") || "";
  return normalized || null;
}

function procurementVariantsAgree(left: SourcingOffer, right: SourcingOffer): boolean {
  // Missing aggregator metadata is not proof that a materially constrained
  // direct SKU is the same commercial offer. Treat only the neutral numeric
  // value of one as equivalent to an omitted constraint.
  const normalizedConstraint = (value: number | null): number | null => (
    value != null && value > 1 ? value : null
  );
  const compatible = <T,>(first: T | null, second: T | null) => first === second;
  return compatible(normalizedPackaging(left.packaging), normalizedPackaging(right.packaging))
    && compatible(normalizedConstraint(left.minimumOrderQuantity), normalizedConstraint(right.minimumOrderQuantity))
    && compatible(normalizedConstraint(left.orderIncrement), normalizedConstraint(right.orderIncrement))
    && compatible(normalizedConstraint(left.packageQuantity), normalizedConstraint(right.packageQuantity));
}

function offerCompletenessScore(offer: SourcingOffer): number {
  const values = [
    offer.unitPrice,
    offer.availableQuantity,
    offer.minimumOrderQuantity,
    offer.orderIncrement,
    offer.packaging,
    offer.shippingCost,
    offer.vatCost,
    offer.importCharges,
    offer.deliveryAt,
    offer.warrantyMonths,
    offer.returnsAccepted,
    offer.description,
    offer.imageUrl,
    offer.datasheetUrl,
  ];
  return values.filter((value) => value !== null && value !== "").length
    + Object.values(offer.costCompleteness).filter(Boolean).length * 2
    + (offer.sellerVerified ? 2 : 0);
}

function observedTime(offer: SourcingOffer): number {
  const value = new Date(offer.observedAt).getTime();
  return Number.isFinite(value) ? value : 0;
}

function mergeDuplicateOffer(group: SourcingOffer[]): SourcingOffer {
  // Commercial fields are an atomic observation: never combine an aggregator
  // price with a direct retailer URL (or vice versa). Freshness wins, then
  // completeness for observations captured at the same time.
  const winner = [...group].sort((left, right) => {
    const freshnessDifference = observedTime(right) - observedTime(left);
    // Source adapters often stamp otherwise simultaneous observations a few
    // milliseconds apart. Within one minute, prefer completeness rather than
    // treating loop order as meaningful freshness.
    if (Math.abs(freshnessDifference) > 60_000) return freshnessDifference;
    return offerCompletenessScore(right) - offerCompletenessScore(left)
      || (isAggregatorOffer(left) ? 1 : 0) - (isAggregatorOffer(right) ? 1 : 0)
      || freshnessDifference
      || left.id.localeCompare(right.id);
  })[0];
  const metadata = [...group].sort((left, right) => (
    offerCompletenessScore(right) - offerCompletenessScore(left)
    || observedTime(right) - observedTime(left)
  ))[0];
  return {
    ...winner,
    // Only descriptive, non-transactional fields may be filled from another
    // observation of the same retailer/MPN.
    manufacturer: winner.manufacturer ?? metadata.manufacturer,
    description: winner.description ?? metadata.description,
    imageUrl: winner.imageUrl ?? metadata.imageUrl,
    datasheetUrl: winner.datasheetUrl ?? metadata.datasheetUrl,
    aggregatedBy: [
      group.some((offer) => offer.sourceKey === "oemsecrets") ? "OEMsecrets" : null,
      group.some((offer) => offer.sourceKey === "nexar") ? "Nexar" : null,
    ].filter(Boolean).join(" / ") || winner.aggregatedBy,
  };
}

function mergeAggregatorDuplicates(group: SourcingOffer[]): SourcingOffer[] {
  const direct = group.filter((offer) => !isAggregatorOffer(offer));
  const aggregators = group.filter(isAggregatorOffer);
  if (direct.length === 0 || aggregators.length === 0) return group;

  const mergeGroups = new Map(direct.map((offer) => [offer.id, [offer]]));
  const unmatchedAggregators: SourcingOffer[] = [];
  for (const aggregator of aggregators) {
    const compatibleDirectOffers = direct.filter((offer) => procurementVariantsAgree(offer, aggregator));
    const exactUrlMatches = compatibleDirectOffers.filter((offer) => offer.productUrl === aggregator.productUrl);
    const candidates = exactUrlMatches.length > 0 ? exactUrlMatches : compatibleDirectOffers;
    if (candidates.length !== 1) {
      unmatchedAggregators.push(aggregator);
      continue;
    }
    mergeGroups.get(candidates[0].id)?.push(aggregator);
  }

  return [
    ...direct.map((offer) => {
      const duplicates = mergeGroups.get(offer.id) ?? [offer];
      return duplicates.length > 1 ? mergeDuplicateOffer(duplicates) : offer;
    }),
    ...unmatchedAggregators,
  ];
}

/**
 * Collapses the same retailer/MPN observation returned through a direct source
 * and an aggregator. Retailer identity remains visible while the newest stock
 * observation and the most complete procurement fields are retained.
 */
export function dedupeSourcingOffers(offers: SourcingOffer[]): SourcingOffer[] {
  const groups = new Map<string, SourcingOffer[]>();
  for (const offer of offers) {
    const key = [
      normalizedRetailerKey(offer),
      normalizeMpn(offer.mpn),
      normalizeOfferCondition(offer.condition),
      offer.requestedQuantity,
    ].join(":");
    const group = groups.get(key);
    if (group) group.push(offer);
    else groups.set(key, [offer]);
  }
  return [...groups.values()].flatMap((group) => {
    return mergeAggregatorDuplicates(group);
  });
}

export function filterOffers(offers: SourcingOffer[], filters: OfferFilters): SourcingOffer[] {
  return offers.filter((offer) => {
    if (!matchesAcceptedOfferConditions(offer.condition, filters.conditions, false)) return false;
    if (filters.confirmedOnly && offer.availabilityConfidence !== "confirmed") return false;
    if (filters.deliveryBy && dateValue(offer.deliveryAt) > dateValue(filters.deliveryBy)) return false;
    if (filters.suppliers?.length && !filters.suppliers.includes(offer.source)) return false;
    if (filters.location && (!offer.location || !offer.location.toLowerCase().includes(filters.location.toLowerCase()))) return false;
    if (filters.warrantyRequired && !offer.warrantyMonths) return false;
    if (filters.maxGbpSubtotal != null && (offer.estimatedGbpSubtotal == null || offer.estimatedGbpSubtotal > filters.maxGbpSubtotal)) return false;
    if (filters.maxGbpTotal != null && (offer.estimatedGbpTotal == null || offer.estimatedGbpTotal > filters.maxGbpTotal)) return false;
    if (filters.controlExchangeCheckoutOnly && offer.checkoutMode !== "control_exchange") return false;
    return true;
  });
}

export function hasCredibleOffer(
  offers: SourcingOffer[],
  requiredBy?: string | null,
): boolean {
  return offers.some((offer) => {
    if (offer.matchType !== "exact") return false;
    if (offer.availabilityConfidence === "unconfirmed") return false;
    if (offer.availableQuantity != null && offer.availableQuantity < offer.orderQuantity) return false;
    if (requiredBy && dateValue(offer.deliveryAt) > dateValue(requiredBy)) return false;
    return true;
  });
}

export function availabilityConfidenceLabel(confidence: AvailabilityConfidence): string {
  if (confidence === "confirmed") return "Stock confirmed";
  if (confidence === "live") return "Live availability reported";
  if (confidence === "feed") return "Feed updated";
  return "Unconfirmed";
}
